import * as React from 'react'
import * as sideNavStyles from './side-nav-section.module.scss'
import { Link } from 'gatsby';
import Chevron from '../icons/chevron';
import { useDelayedEffect } from '../../hooks/delayed-effect';
import { useCurrentNavSegments } from '../../hooks/current-nav-segments';
import HighlightedText from '../highlighted-text/highlighted-text';
import { PremiumTag } from '../premium-tag/premium-tag';

const isOutsideView = (element) => {
    const midPoint = Math.ceil(element.offsetTop + (element.offsetHeight / 2));
    const result = midPoint < element.offsetParent.scrollTop || midPoint > element.offsetParent.scrollTop + element.offsetParent.offsetHeight;
    return result
}

const scrollParentToElement = (element) => {
    const midPoint = Math.ceil(element.offsetTop + (element.offsetHeight / 2));
    const parent = element.offsetParent;

    const newScrollY = Math.max(0, Math.ceil(midPoint - (parent.offsetHeight / 2)));

    parent.scrollTo({ top: newScrollY });
}

const isVisible = (node, filterContext) => {
    if (!filterContext) {
        return !node.hidden;
    }

    const result = !node.hidden && filterContext.visible.has(node)
    return result;
}

const isExpanded = (node, filterContext) => {
    if (!filterContext) {
        return false;
    }

    const result = filterContext.expanded.has(node);
    return result;
}

const SideNavSection = ({ navNode, level, filterContext, capitalize, hidePremiumTag }) => {
    const sectionRef = React.useRef(null)

    const [expanded, setExpanded] = React.useState(false);

    const [isActive, setIsActive] = React.useState(false);

    const currentNavSegments = useCurrentNavSegments();

    React.useEffect(() => {
        const nodeIndex = currentNavSegments.indexOf(navNode);
        const hasVisibleChildren = !navNode.hideChildren && navNode.childrenSegments?.filter(s => !s.hidden).length > 0;

        setIsActive(isVisible(navNode, filterContext) && (nodeIndex === 0 || (!hasVisibleChildren && nodeIndex > 0)));
        setExpanded(isVisible(navNode, filterContext) && (nodeIndex > 0 || isExpanded(navNode, filterContext)));
    }, [currentNavSegments, navNode, filterContext]);

    useDelayedEffect(() => {
        if (filterContext) {
            return;
        }

        if (!sectionRef.current) {
            return;
        }

        if (sectionRef.current.offsetParent && isActive && isOutsideView(sectionRef.current)) {
            scrollParentToElement(sectionRef.current);
        }
    }, [isActive, filterContext]);

    const lvl = level || 0;
    let linkStyle = { paddingLeft: `${21 + 16 * (lvl + 1)}px` }

    const sectionClick = (e) => {
        e.preventDefault();
        setExpanded(!expanded);
    }

    const activeClass = isActive ? sideNavStyles.active : '';

    const childrenHidePremium = React.useMemo(() => {
        const result = navNode.tier === `premium` && !navNode?.childrenSegments.find(s => s.tier !== `premium`)
        return result;
    }, [navNode]);

    return (<>
        {isVisible(navNode, filterContext) && <div ref={sectionRef} className={`${sideNavStyles.section} ${expanded ? sideNavStyles.expanded : ''}`}>
            {!navNode.hideChildren && navNode.childrenSegments?.filter(s => !s.hidden).length > 0 && <>
                <button style={linkStyle} className={`${sideNavStyles.group}`} onClick={sectionClick} aria-label={navNode.title}>
                    <div className={`${sideNavStyles.groupText} ${capitalize ? sideNavStyles.capitalize : ''}`} data-tag={navNode.tag}>
                        <Chevron className={sideNavStyles.expander} />
                        <HighlightedText value={navNode.title} highlight={filterContext?.text}></HighlightedText>
                        { !hidePremiumTag && navNode.tier === `premium` && <PremiumTag /> }
                    </div>
                </button>
            </>}
            {(navNode.hideChildren || !navNode.childrenSegments || navNode.childrenSegments.filter(s => !s.hidden).length <= 0)
                && <Link style={linkStyle} className={`${sideNavStyles.page} ${activeClass} ${capitalize ? sideNavStyles.capitalize : ''}`} data-tag={navNode.tag} to={navNode.relativeUrl || navNode.childrenSegments[0]?.relativeUrl || ``}>
                        <HighlightedText value={navNode.title} highlight={filterContext?.text}></HighlightedText>
                        { !hidePremiumTag && navNode.tier === `premium` && <PremiumTag /> }
                   </Link>}
            {!navNode.hideChildren && expanded && navNode.childrenSegments.filter(s => !s.hidden).map(s => <SideNavSection filterContext={filterContext} key={s.title} navNode={s} level={lvl + 1} hidePremiumTag={childrenHidePremium}></SideNavSection>)}
        </div>}
    </>)
}

export default SideNavSection;